import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';

export function RegisterPage() {
  const [form, setForm] = useState({ email: '', password: '', firstName: '', lastName: '' });
  const { register, error, isLoading, clearError } = useAuthStore();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await register(form);
      navigate('/dashboard');
    } catch {}
  };

  const update = (field: string, value: string) => setForm({ ...form, [field]: value });

  // M5: warm-ivory retheme — mirrors LoginPage fix (commit 5346dc2). The legacy
  // bg-[var(--ink-0)] / bg-gray-50 classes resolved to invisible buttons after the
  // tokens roll-out; direct CSS-var inline styles guarantee high contrast.
  const inputStyle: React.CSSProperties = {
    background: 'var(--ivory-0)',
    color: 'var(--ink-0)',
    border: '1px solid var(--line)',
  };

  return (
    <div
      className="aura-root flex min-h-screen items-center justify-center px-4"
      style={{ background: 'var(--ivory-0)' }}
    >
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <div
            className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl text-xl font-bold"
            style={{
              background: 'linear-gradient(135deg, var(--ink-0), var(--ink-1))',
              color: 'var(--ivory-0)',
              fontFamily: 'var(--ff-serif)',
            }}
          >
            A
          </div>
          <h1
            className="mt-4 text-2xl"
            style={{
              fontFamily: 'var(--ff-serif)',
              fontWeight: 600,
              color: 'var(--ink-0)',
              letterSpacing: '-0.01em',
            }}
          >
            Create your AURA account
          </h1>
          <p className="mt-1 text-sm" style={{ color: 'var(--ink-3)' }}>
            AI Unified Resource Architecture
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl p-8"
          style={{
            background: 'var(--ivory-1)',
            border: '1px solid var(--line)',
            boxShadow: 'var(--sh-2)',
          }}
        >
          {error && (
            <div
              className="mb-4 rounded-lg p-3 text-sm"
              style={{ background: 'var(--danger-bg)', color: 'var(--danger)' }}
            >
              {error}
              <button onClick={clearError} className="ml-2 font-medium underline">
                Dismiss
              </button>
            </div>
          )}

          <div className="mb-4 grid grid-cols-2 gap-3">
            <div>
              <label
                className="mb-1.5 block text-sm font-medium"
                style={{ color: 'var(--ink-1)' }}
              >
                First Name
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                required
                className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
                style={inputStyle}
              />
            </div>
            <div>
              <label
                className="mb-1.5 block text-sm font-medium"
                style={{ color: 'var(--ink-1)' }}
              >
                Last Name
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                required
                className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
                style={inputStyle}
              />
            </div>
          </div>

          <div className="mb-4">
            <label
              className="mb-1.5 block text-sm font-medium"
              style={{ color: 'var(--ink-1)' }}
            >
              Email
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => update('email', e.target.value)}
              required
              className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
              style={inputStyle}
              placeholder="you@company.com"
            />
          </div>

          <div className="mb-6">
            <label
              className="mb-1.5 block text-sm font-medium"
              style={{ color: 'var(--ink-1)' }}
            >
              Password
            </label>
            <input
              type="password"
              value={form.password}
              onChange={(e) => update('password', e.target.value)}
              required
              minLength={8}
              className="w-full rounded-lg px-3.5 py-2.5 text-sm focus:outline-none"
              style={inputStyle}
              placeholder="Minimum 8 characters"
            />
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50"
            style={{
              background: 'var(--ink-0)',
              color: 'var(--ivory-0)',
            }}
            onMouseOver={(e) => {
              if (!isLoading)
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--ink-1)';
            }}
            onMouseOut={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--ink-0)';
            }}
          >
            {isLoading ? 'Creating account...' : 'Create account'}
          </button>

          <p className="mt-4 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-medium"
              style={{ color: 'var(--saffron-600)' }}
            >
              Sign in
            </Link>
          </p>
        </form>

        <p className="mt-6 text-center text-xs text-gray-400">
          Powered by Ardhanex Tech Pvt Ltd
        </p>
      </div>
    </div>
  );
}
